<template>
  <section class="admin-content" id="tickets-index">
    <Navbar />
    <main class="admin-main">
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center justify-content-between text-white" style="padding: 32px 0;">
            <div class="d-flex align-items-center" style="gap: 16px;">
              <div class="header-icon-wrap">
                <i class="fe fe-message-circle" style="font-size: 22px;"></i>
              </div>
              <div>
                <h4 class="mb-0 font-weight-bold">All Tickets</h4>
                <small style="opacity: 0.8;">Track complaint intake, assignments, and resolution</small>
              </div>
            </div>
            <router-link v-if="canCreateTickets" to="/add-complaint" class="btn btn-light btn-sm px-3">
              <i class="fe fe-plus mr-1"></i> Create Ticket
            </router-link>
          </div>
        </div>
      </div>

      <section style="margin-top: -20px;">
        <div class="container pb-5">
          <div class="row mb-4">
            <div class="col-md-4 mb-3 mb-md-0">
              <div class="card shadow-sm metric-card">
                <div class="card-body">
                  <p class="metric-label mb-1">Total Tickets</p>
                  <h4 class="metric-value mb-0">{{ stats.total }}</h4>
                </div>
              </div>
            </div>
            <div class="col-md-4 mb-3 mb-md-0">
              <div class="card shadow-sm metric-card metric-active">
                <div class="card-body">
                  <p class="metric-label mb-1">Active</p>
                  <h4 class="metric-value mb-0">{{ stats.active }}</h4>
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="card shadow-sm metric-card metric-resolved">
                <div class="card-body">
                  <p class="metric-label mb-1">Resolved</p>
                  <h4 class="metric-value mb-0">{{ stats.resolved }}</h4>
                </div>
              </div>
            </div>
          </div>

          <div class="card shadow-sm form-card mb-4" v-if="showAgencyFilters">
            <div class="form-card-header">
              <div class="section-number"><i class="fe fe-filter" style="font-size: 12px;"></i></div>
              <h6 class="mb-0 font-weight-bold text-white">Filter Records</h6>
            </div>
            <div class="card-body px-4 py-4">
              <div class="form-row align-items-end">
                <div class="form-group col-md-4">
                  <label class="small font-weight-bold text-secondary">Provider</label>
                  <select class="form-control" v-model="filters.provider_id">
                    <option value="">All providers</option>
                    <option v-for="provider in providers" :key="provider.id" :value="provider.id">
                      {{ providerLabel(provider) }}
                    </option>
                  </select>
                </div>
                <div class="form-group col-md-3">
                  <label class="small font-weight-bold text-secondary"><i class="fe fe-calendar mr-1"></i>Start Date</label>
                  <input type="date" class="form-control" v-model="filters.start_date" />
                </div>
                <div class="form-group col-md-3">
                  <label class="small font-weight-bold text-secondary"><i class="fe fe-calendar mr-1"></i>End Date</label>
                  <input type="date" class="form-control" v-model="filters.end_date" />
                </div>
                <div class="form-group col-md-2 d-flex" style="gap: 8px;">
                  <button class="btn btn-submit btn-block" @click="applyFilters">
                    <i class="fe fe-search mr-1"></i> Filter
                  </button>
                  <button class="btn btn-outline-secondary btn-block" @click="resetFilters">Reset</button>
                </div>
              </div>
            </div>
          </div>

          <div class="card shadow-sm table-card">
            <div class="form-card-header">
              <div class="section-number"><i class="fe fe-list" style="font-size: 12px;"></i></div>
              <h6 class="mb-0 font-weight-bold text-white">Ticket Queue</h6>
              <span class="badge badge-light ml-auto" style="border-radius: 20px;">{{ complaints.total }} ticket(s)</span>
            </div>

            <div class="table-responsive">
              <table class="table align-td-middle mb-0">
                <thead style="background: #f8fafc;">
                  <tr>
                    <th class="table-th">#</th>
                    <th class="table-th">Title</th>
                    <th class="table-th">Enrollee</th>
                    <th class="table-th">Type</th>
                    <th class="table-th">Status</th>
                    <th class="table-th">Provider</th>
                    <th class="table-th">Assigned Staff</th>
                    <th class="table-th">Date</th>
                    <th class="table-th"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in complaints.data" :key="item.id" class="table-row">
                    <td class="px-4 py-3 text-muted small">{{ index + 1 }}</td>
                    <td class="py-3 font-weight-bold">{{ item.title }}</td>
                    <td class="py-3 text-muted">{{ enrolleeName(item) }}</td>
                    <td class="py-3"><span class="type-chip">{{ item.type }}</span></td>
                    <td class="py-3"><span class="status-badge" :class="statusClass(item.status)">{{ formatStatus(item.status) }}</span></td>
                    <td class="py-3 text-muted">{{ providerName(item.provider_id) }}</td>
                    <td class="py-3 text-muted">{{ assignedStaffNames(item) }}</td>
                    <td class="py-3 text-muted">{{ formatDate(item.created_at) }}</td>
                    <td class="py-3">
                      <router-link :to="{ path: '/complaint/' + item.id }">
                        <button type="button" class="btn btn-sm btn-outline-info">
                          <i class="fe fe-eye mr-1"></i> View
                        </button>
                      </router-link>
                    </td>
                  </tr>
                  <tr v-if="!complaints.data.length">
                    <td colspan="9" class="text-center py-5 text-muted">
                      <i class="fe fe-inbox" style="font-size: 2rem; opacity: 0.3;"></i>
                      <p class="mt-2 mb-0">No tickets found</p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="card-body pt-2" v-if="complaints.last_page > 1">
              <b-pagination v-model="page" :total-rows="complaints.total" :per-page="complaints.per_page || 15"
                @input="getComplaints(page)"></b-pagination>
            </div>
          </div>
        </div>

        <div class="vld-parent">
          <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
        </div>
      </section>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";

export default {
  components: { Navbar, Loading },
  data() {
    return {
      agency: JSON.parse(localStorage.getItem("agency")),
      user: JSON.parse(localStorage.getItem("user")),
      isLoading: false,
      fullPage: true,
      providers: [],
      employees: [],
      page: 1,
      complaints: {
        data: [],
        total: 0,
        last_page: 1,
        per_page: 15,
      },
      stats: {
        total: 0,
        active: 0,
        resolved: 0,
        completed: 0,
      },
      filters: {
        provider_id: "",
        start_date: "",
        end_date: "",
      },
    };
  },
  computed: {
    showAgencyFilters() {
      return this.user && this.user.type !== "client";
    },
    canCreateTickets() {
      if (!this.user) {
        return false;
      }

      return ["employee", "shis", "provider", "provider_employee"].includes(this.user.type) || this.user.user_role == 1;
    },
  },
  methods: {
    normalizeCollection(payload) {
      if (Array.isArray(payload)) {
        return payload;
      }

      if (payload && Array.isArray(payload.data)) {
        return payload.data;
      }

      return [];
    },
    providerLabel(provider) {
      return provider.agency_name || provider.name || provider.provider_name || provider.full_name || provider.firstname || `Provider #${provider.id}`;
    },
    providerName(providerId) {
      if (!providerId) {
        return "N/A";
      }

      const provider = this.providers.find((item) => String(item.id) === String(providerId));
      return provider ? this.providerLabel(provider) : `Provider #${providerId}`;
    },
    enrolleeName(item) {
      if (item.enrollee) {
        return `${item.enrollee.firstname || ""} ${item.enrollee.lastname || ""}`.trim();
      }

      return `Enrollee #${item.user_id}`;
    },
    // Names come from the same HR employee list (and firstname + lastname
    // convention) used on the Employee Management page, not whatever the
    // ticket itself embeds, so a staff member's name always matches there.
    employeeName(staffId) {
      const employee = this.employees.find((item) => String(item.id) === String(staffId));
      if (!employee) {
        return null;
      }

      return `${employee.firstname || ""} ${employee.lastname || ""}`.trim() || null;
    },
    assignedStaffNames(item) {
      const list = item.assigned_staff || item.staff || [];
      if (!list.length) {
        return "Unassigned";
      }

      return list
        .map((staff) => {
          const id = staff && typeof staff === "object" ? staff.id : staff;
          return this.employeeName(id)
            || (staff && staff.full_name)
            || (staff && `${staff.firstname || ""} ${staff.lastname || ""}`.trim())
            || `Staff #${id}`;
        })
        .join(", ");
    },
    statusClass(status) {
      if (["resolved", "closed"].includes(status)) {
        return "status-resolved";
      }

      if (status === "completed") {
        return "status-completed";
      }

      if (status === "open") {
        return "status-open";
      }

      return "status-unanswered";
    },
    formatStatus(status) {
      if (!status) {
        return "Unknown";
      }

      if (status === "closed") {
        return "Resolved";
      }

      return status.charAt(0).toUpperCase() + status.slice(1);
    },
    formatDate(value) {
      if (!value) {
        return "-";
      }

      return new Date(value).toLocaleDateString();
    },
    currentParams() {
      return {
        provider_id: this.filters.provider_id || undefined,
        start_date: this.filters.start_date || undefined,
        end_date: this.filters.end_date || undefined,
        page: this.page,
      };
    },
    fetchProviders() {
      const agencyId = (this.user && this.user.institutional_id) || (this.agency && this.agency.id);
      if (!agencyId) return;
      this.axios
        .get(`/api/v1/auth/providerAgency/${agencyId}`)
        .then((response) => {
          this.providers = this.normalizeCollection(response.data);
        })
        .catch((error) => {
          console.error(error);
        });
    },
    fetchEmployees() {
      const agencyId = this.agency && this.agency.id;
      if (!agencyId) return;
      this.axios
        .get(`/api/v1/auth/getEmployee/${agencyId}`)
        .then((response) => {
          this.employees = this.normalizeCollection(response.data);
        })
        .catch((error) => {
          console.error(error);
        });
    },
    getComplaints(page) {
      this.isLoading = true;
      this.page = page || 1;

      if (this.user.type === "client") {
        this.axios
          .get(`/api/v1/auth/complaints/${this.user.id}`, { params: { page: this.page } })
          .then((response) => {
            this.complaints = {
              data: response.data.data || [],
              total: response.data.total || (response.data.data || []).length,
              last_page: response.data.last_page || 1,
              per_page: response.data.per_page || 15,
            };
            this.stats = {
              total: this.complaints.total,
              active: this.complaints.data.filter((item) => !["resolved", "completed", "closed"].includes(item.status)).length,
              resolved: this.complaints.data.filter((item) => ["resolved", "closed"].includes(item.status)).length,
              completed: this.complaints.data.filter((item) => item.status === "completed").length,
            };
            this.isLoading = false;
          })
          .catch((error) => {
            console.error(error);
            this.isLoading = false;
          });

        return;
      }

      this.axios
        .get(`/api/v1/auth/complaints/agency/${this.agency.id}`, { params: this.currentParams() })
        .then((response) => {
          this.complaints = {
            data: response.data.data || [],
            total: response.data.total || (response.data.data || []).length,
            last_page: response.data.last_page || 1,
            per_page: response.data.per_page || 15,
          };
          this.isLoading = false;
        })
        .catch((error) => {
          console.error(error);
          this.isLoading = false;
        });
    },
    getStats() {
      if (!this.showAgencyFilters) {
        return;
      }

      this.axios
        .get(`/api/v1/auth/complaints/stats/${this.agency.id}`, { params: this.currentParams() })
        .then((response) => {
          this.stats = response.data;
        })
        .catch((error) => {
          console.error(error);
        });
    },
    applyFilters() {
      this.getComplaints();
      this.getStats();
    },
    resetFilters() {
      this.filters = {
        provider_id: "",
        start_date: "",
        end_date: "",
      };
      this.applyFilters();
    },
  },
  created() {
    if (this.showAgencyFilters) {
      this.fetchProviders();
      this.fetchEmployees();
      this.getStats();
    }
    this.getComplaints();
  },
};
</script>

<style scoped>
.page-header {
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  padding-bottom: 40px;
}

.header-icon-wrap {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.form-card,
.metric-card,
.table-card {
  border-radius: 12px;
  border: 1px solid #e9ecef;
  overflow: hidden;
}

.form-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
}

.section-number {
  width: 28px;
  height: 28px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
}

.btn-submit {
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  border: none;
  color: #fff;
  border-radius: 8px;
  font-weight: 600;
}

.btn-submit:hover {
  opacity: 0.9;
  color: #fff;
}

.metric-card {
  border-radius: 12px;
  border: 1px solid #e9ecef;
}

.metric-active {
  border-left: 4px solid #f59e0b;
}

.metric-resolved {
  border-left: 4px solid #16a34a;
}

.metric-label {
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  font-weight: 700;
}

.metric-value {
  font-weight: 700;
  color: #0f172a;
}

.table-th {
  font-size: 0.73rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  font-weight: 600;
  border-bottom: 2px solid #e2e8f0;
  padding: 12px 16px;
}

.table-row:hover {
  background: #f8fafc;
}

.type-chip {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  background: #eef2ff;
  color: #4338ca;
  font-size: 0.75rem;
  font-weight: 600;
}

.status-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
}

.status-unanswered {
  background: #f1f5f9;
  color: #64748b;
}

.status-open {
  background: #fef3c7;
  color: #92400e;
}

.status-resolved {
  background: #d1fae5;
  color: #065f46;
}

.status-completed {
  background: #dbeafe;
  color: #1d4ed8;
}
</style>
